import { TransitionAfterIntroductionDateRange } from '../../utils/transitionAfterIntroductionDateRange';
import { GraphDateType } from '../dataKiGraph';
import { DkSisSearchParams } from '../dkSis';
import {
  resolveGraphSlotChartHeight,
  resolveGraphSlotContainerHeight,
} from '../graphSlotLayout';
import {
  Column,
  Item,
  MainFieldType,
  Option,
  OrderType,
  SearchCondition,
} from '../schemas';
import { GraphData } from '../schemas';
import { ShuOption } from '../shu';
import { transitiveDateTypes } from './consistent';

/** 推移グラフ（機種比較）で同時表示できるグラフ項目の最大数 */
export const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_MAX_FIELDS = 5;

/** 導入後推移の複数グラフで recharts syncId に渡す固定値 */
export const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_SYNC_ID =
  'transitionAfterIntroductionKiGraph';

export const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CHART_HEIGHT = 360;
export const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_COMPACT_CHART_HEIGHT = 200;
export const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_DENSE_CHART_HEIGHT = 140;
export const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_OVERFLOW_CHART_HEIGHT = 120;

export const resolveDataTransitionAfterIntroductionGraphChartHeight = (
  graphCount: number
): number =>
  resolveGraphSlotChartHeight(graphCount, {
    single: DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CHART_HEIGHT,
    double: DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_COMPACT_CHART_HEIGHT,
    triple: DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_DENSE_CHART_HEIGHT,
    overflow: DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_OVERFLOW_CHART_HEIGHT,
  });

const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_CHROME_HEIGHT = 48;
const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_TITLE_HEIGHT = 48;
const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_CONTROLS_HEIGHT = 104;
const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_SHARED_LEGEND_HEIGHT = 40;
const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT = 56;
const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_SLOT_GAP = 16;
const DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_FOOTER_HEIGHT = 52;

export const resolveDataTransitionAfterIntroductionGraphContainerHeight = (
  slotCount: number,
  options?: { hasMultipleGraphs?: boolean }
): number =>
  resolveGraphSlotContainerHeight(
    slotCount,
    {
      chromeHeight:
        DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_CHROME_HEIGHT,
      titleHeight:
        DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_TITLE_HEIGHT,
      controlsHeight:
        DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_CONTROLS_HEIGHT,
      slotControlsHeight:
        DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT,
      slotGap: DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_SLOT_GAP,
      footerHeight:
        DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_FOOTER_HEIGHT,
      sharedLegendHeight:
        DATA_TRANSITION_AFTER_INTRODUCTION_GRAPH_CONTAINER_SHARED_LEGEND_HEIGHT,
      resolveChartHeight:
        resolveDataTransitionAfterIntroductionGraphChartHeight,
    },
    options
  );

export type TransitionAfterIntroductionReportsSettingDateRangeParams = {
  /**
   * 現在選択中の期間
   */
  dateRange: TransitionAfterIntroductionDateRange;
};

export type DataTransitionAfterIntroductionGraphParams = {
  containsAverage?: boolean;
  seriesType?: 'hall' | 'ki';
  /**
   * グラフ API のクエリ param `field`（1項目分）。
   * 複数グラフ表示以前のお気に入りはこちらが保存されている。
   */
  field?: string;
  /**
   * 画面に出すグラフ項目の一覧。
   * お気に入り保存用。API リクエストでは使わず、Saga が1件ずつ field に分けて送る。
   */
  fields?: string[];
  dateType?: GraphDateType;
  kiList?: string[];
  kiListForSijiritu?: string[];
  /**
   * 本来はOpenAPI側でsring[]として定義されているが、現在展開行からのグラフ表示が存在しないためunknownとして定義している
   */
  hallsForSijiritu?: unknown;
  shuGroupIds?: string[];
  shuList?: string[];
  areas?: string[];
  halls?: string[];
  makers?: string[];
  sisTypes?: string[];
  startDate?: string;
  endDate?: string;
  excludeToday?: boolean;
};

export type DataTransitionAfterIntroductionGraphFieldItem = {
  field: string;
  data: GraphData;
};

/** `/data/transitionAfterIntroduction/graph` 1回分のレスポンス（field 1項目あたり） */
export type DataTransitionAfterIntroductionGraphApiResponse = {
  setting: DataTransitionAfterIntroductionGraphParams;
  data: GraphData;
};

export type DataTransitionAfterIntroductionGraphSetting = Omit<
  DataTransitionAfterIntroductionGraphParams,
  'field' | 'fields'
>;

export type DataTransitionAfterIntroductionGraph = {
  setting: DataTransitionAfterIntroductionGraphSetting;
  graphList: DataTransitionAfterIntroductionGraphFieldItem[];
};

export const toDataTransitionAfterIntroductionGraphSetting = (
  params: DataTransitionAfterIntroductionGraphParams
): DataTransitionAfterIntroductionGraphSetting => {
  const { field: _field, fields: _fields, ...setting } = params;
  return setting;
};

export type TransitiveDateType = (typeof transitiveDateTypes)[number];

export type DataTransitionAfterIntroductionParams = {
  mainField?: MainFieldType;
  transitiveFields?: string[];
  nonTransitiveFields?: string[];
  marking?: string;
  isFiltering?: boolean;
  dateType?: TransitiveDateType;
  kiList?: string[];
  kiListForSijiritu?: string[];
  shuList?: string[];
  halls?: string[];
  makers?: string[];
  sisTypes?: string[];
  areas?: string[];
  startDate?: string;
  endDate?: string;
  excludeToday?: boolean;
  shuGroupIds?: string[];
  sortForHeader?: string;
  sortForTransitiveField?: string;
  order?: OrderType;
} & DkSisSearchParams;

export type DataTransitionAfterIntroductionData = {
  columnsForNonTransition: Column[];
  columnsForTransition: Column[];
  dates: {
    ordinal: string;
    code: string;
    backgroundColor: string | null;
  }[];
  rows: {
    queryParameter: {
      kiList: string[];
      halls: string[];
    };
    dataForNonTransition: Item[];
    dataForTransition: Item[][];
  }[];
};

export type DataTransitionAfterIntroduction = {
  setting: Required<DataTransitionAfterIntroductionParams> & {
    /**
     * レスポンス先頭が平均・合計行かどうか。検索パラメータには無い。
     */
    containsAverage?: boolean;
  };
  data: DataTransitionAfterIntroductionData;
};

export type SettingsOptionsTransitionAfterIntroduction = {
  searchCondition: SearchCondition;
  fields: {
    transition: Option[];
    nonTransition: Option[];
  };
  genres: {
    transition: string[];
  };
};

/**
 * 導入後推移のお気に入りの型のベース(型の再定義が必要になった際はこの型を継承して利用してください)
 */
export type TransitionAfterIntroductionBaseFavorite = {
  /**
   * 検索フォームの検索条件
   */
  searchCondition: DataTransitionAfterIntroductionParams;
  /**
   * 検索フォームで選択中の期間
   */
  selectedDateRangeParams: TransitionAfterIntroductionReportsSettingDateRangeParams;
  /**
   * 検索フォームで選択した店舗一覧
   */
  selectedHalls?: string[];
  /**
   * 検索フォームで選択した種別一覧
   */
  selectedShu: ShuOption[];
  /**
   * 表示中の店舗
   */
  currentHalls?: string[];
  /**
   * テーブルで選択中の種別
   */
  currentShu: ShuOption;
  /**
   * 導入後推移テーブルの検索条件
   */
  dataTransitionAfterIntroduction?: DataTransitionAfterIntroductionParams;
  /**
   * 導入後推移テーブルの展開行の検索条件
   */
  dataTransitionAfterIntroduction2ndRow?: {
    [key: string]: DataTransitionAfterIntroductionParams;
  };
  /**
   * 導入後推移の推移グラフ（機種比較）
   */
  dataTransitionAfterIntroductionKiGraph?: {
    /**
     * 検索条件
     */
    setting: DataTransitionAfterIntroductionGraphParams;
    /**
     * セレクターで選択された機種
     */
    selectKi: string[];
  };
  /**
   * フィルターされた機種リスト
   */
  kiList: string[];
  /**
   * 導入後推移テーブルの推移項目並び順
   */
  columnForTransitionOrder: string[];
  /**
   * 導入後推移テーブルの非推移項目並び順
   */
  columnForNonTransitionOrder: string[];
  /**
   * 導入後推移の機種名フィルタ
   */
  modelNameFilter: string;
  /**
   * タイプ列の複数選択。過去のお気に入りには存在しない
   */
  tableHeaderSubMenuMultiFilter?: {
    [columnName: string]: string[];
  };
  /**
   * テーブルの非表示項目一覧
   */
  tableFilterItems: string[];
  /**
   * チェックされた機種の種別ごとのテーブル
   */
  checkedKiListTable: { [shuCode: string]: string[] };
  /**
   * ヒートマップ表示の有効無効
   */
  isHeatmapEnabled?: boolean;
  /**
   * @deprecated 過去のお気に入りデータのマイグレーション用に残しています、新規利用はしないでください。
   */
  selectedCurrentFieldCode?: string;
  /**
   * 項目絞込の選択された項目
   */
  selectedCurrentFieldCodes?: string[];
  /**
   * ページネーションの状態
   */
  pagination?: {
    main: {
      page: number;
      rowsPerPage: number;
    };
  };
};

/**
 * 導入後推移の最新のお気に入りの型
 */
export type TransitionAfterIntroductionFavorite = Omit<
  TransitionAfterIntroductionBaseFavorite,
  'selectedCurrentFieldCode'
>;
